import { ArrowDown, Wrench } from "lucide-react";
import Logo from "@/components/site/Logo";

export default function Hero() {
    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative min-h-[88vh] flex flex-col items-center justify-center overflow-hidden bg-[#0f0f12]"
        >
            {/* Layer 1 — base + soft brand glows */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse 70% 55% at 18% 15%, rgba(213,43,30,0.22) 0%, rgba(213,43,30,0) 60%), radial-gradient(ellipse 65% 55% at 85% 85%, rgba(40,72,140,0.25) 0%, rgba(40,72,140,0) 60%), radial-gradient(ellipse at center, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0) 70%)",
                }}
                aria-hidden
            />

            {/* Layer 2 — detailed hex pattern with red accent hexes */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='254' viewBox='0 0 220 254'><defs><pattern id='h' x='0' y='0' width='220' height='254' patternUnits='userSpaceOnUse'><g fill='none' stroke-linejoin='round'><polygon points='55,12 95,35 95,81 55,104 15,81 15,35' stroke='%23ffffff' stroke-width='1' opacity='0.10'/><polygon points='165,12 205,35 205,81 165,104 125,81 125,35' stroke='%23ffffff' stroke-width='1' opacity='0.06'/><polygon points='110,116 150,139 150,185 110,208 70,185 70,139' stroke='%23d52b1e' stroke-width='1.2' opacity='0.22'/><polygon points='0,116 40,139 40,185 0,208 -40,185 -40,139' stroke='%23ffffff' stroke-width='1' opacity='0.08'/><polygon points='220,116 260,139 260,185 220,208 180,185 180,139' stroke='%23ffffff' stroke-width='1' opacity='0.08'/></g></pattern></defs><rect width='220' height='254' fill='url(%23h)'/></svg>\")",
                    backgroundSize: "440px 508px",
                }}
                aria-hidden
            />

            {/* Layer 3 — fine dot grid overlay for tech texture */}
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.35]"
                style={{
                    backgroundImage:
                        "radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)",
                    backgroundSize: "28px 28px",
                    maskImage:
                        "radial-gradient(ellipse at center, black 30%, transparent 75%)",
                    WebkitMaskImage:
                        "radial-gradient(ellipse at center, black 30%, transparent 75%)",
                }}
                aria-hidden
            />

            {/* Layer 4 — diagonal scanlines (very subtle metallic sheen) */}
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.05]"
                style={{
                    backgroundImage:
                        "repeating-linear-gradient(115deg, rgba(255,255,255,0.7) 0px, rgba(255,255,255,0.7) 1px, transparent 1px, transparent 4px)",
                }}
                aria-hidden
            />

            {/* Layer 5 — film grain / noise for that industrial feel */}
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.15] mix-blend-overlay"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                }}
                aria-hidden
            />

            {/* Layer 6 — bottom + top fades for content readability */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(to bottom, rgba(15,15,18,0.5) 0%, rgba(15,15,18,0) 15%, rgba(15,15,18,0) 88%, rgba(15,15,18,0.95) 100%)",
                }}
                aria-hidden
            />

            {/* Layer 7 — soft red spotlight behind logo */}
            <div
                className="absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle, rgba(213,43,30,0.14) 0%, rgba(213,43,30,0) 55%)",
                    filter: "blur(50px)",
                }}
                aria-hidden
            />
            {/* Hazard stripe top */}
            <div className="absolute top-0 left-0 w-full h-2 hazard-stripes opacity-90 z-10" />

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 py-14 w-full flex flex-col items-center text-center">
                <div className="inline-flex items-center gap-2 border border-white/25 px-4 py-1.5 mb-10 bg-black/40 backdrop-blur-sm rounded-full">
                    <Wrench className="w-3.5 h-3.5 text-[#d52b1e]" />
                    <span className="text-[10px] uppercase tracking-[0.35em] text-zinc-200 font-semibold">
                        Specialist Car Dismantlers
                    </span>
                </div>

                {/* Custom branded logo */}
                <Logo variant="full" className="mb-10" />

                {/* Divider */}
                <div className="flex items-center gap-4 mb-8">
                    <span className="h-px w-16 bg-white/20" />
                    <span className="w-1.5 h-1.5 bg-[#d52b1e]" />
                    <span className="h-px w-16 bg-white/20" />
                </div>

                <p className="text-zinc-300 max-w-2xl text-lg leading-relaxed">
                    Browse our live stock of dismantled vehicles. Engines, gearboxes, body
                    panels, lights, interiors — tested, photographed, and ready to ship
                    across Europe.
                </p>

                <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                    <button
                        onClick={() => scrollTo("stock")}
                        data-testid="hero-view-stock-btn"
                        className="bg-[#d52b1e] hover:bg-[#f03022] text-white px-8 py-4 uppercase text-xs tracking-[0.3em] font-bold transition-colors inline-flex items-center gap-2 shadow-[0_10px_30px_-8px_rgba(213,43,30,0.6)]"
                    >
                        View live stock
                        <ArrowDown className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => scrollTo("contact")}
                        data-testid="hero-contact-btn"
                        className="bg-white/5 hover:bg-white/10 border border-white/30 hover:border-white text-white px-8 py-4 uppercase text-xs tracking-[0.3em] font-bold transition-colors backdrop-blur-sm"
                    >
                        Request a part
                    </button>
                </div>

                <div className="mt-14 grid grid-cols-3 gap-12 md:gap-20 border-t border-white/10 pt-8 max-w-2xl">
                    {[
                        { k: "20+", v: "Years\nExperience" },
                        { k: "500+", v: "Vehicles\nDismantled" },
                        { k: "48h", v: "Fast\nDispatch" },
                    ].map((s) => (
                        <div key={s.k}>
                            <div className="font-display text-4xl md:text-5xl text-white leading-none">{s.k}</div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 whitespace-pre-line mt-2 font-semibold">
                                {s.v}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Hazard stripe bottom */}
            <div className="absolute bottom-0 left-0 w-full h-2 hazard-stripes opacity-90 z-10" />
        </section>
    );
}
